"use client"

import styled from "@emotion/styled"
import { HostServiceResponse } from "api"
import { useTranslations } from "next-intl"
import Image from "next/image"
import React, { useEffect, useMemo, useRef, useState } from "react"
import { XIcon } from "../icons"

const StyledWrapper = styled.div`
  position: relative;
  width: 100%;
`

const StyledTrigger = styled.button<{ $isOpen: boolean; $hasValue: boolean }>`
  width: 100%;
  height: 48px;
  padding: 8px 12px;
  border: 1.2px solid ${({ $isOpen }) => ($isOpen ? "#0e2846" : "#e6e6e6")};
  border-radius: ${({ $isOpen }) => ($isOpen ? "8px 8px 0 0" : "8px")};
  background: white;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-family: inherit;

  .trigger-text {
    flex: 1;
    font-size: 16px;
    font-weight: ${({ $hasValue }) => ($hasValue ? 700 : 300)};
    color: ${({ $hasValue }) => ($hasValue ? "#0e2846" : "#687079")};
  }
`

const StyledClearButton = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  flex-shrink: 0;

  svg {
    width: 20px;
    height: 20px;
  }
`

const StyledDropdown = styled.div`
  background: white;
  border: 1.2px solid #e6e6e6;
  border-top: none;
  border-radius: 0 0 8px 8px;
  box-shadow: 0px 4px 12px rgba(42, 42, 42, 0.12);
  max-height: 367px;
  overflow-y: auto;
  width: 100%;
  padding: 8px;
  display: flex;
  flex-direction: column;
`

const StyledItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px 12px;
  border-bottom: 1px solid #e6e6e6;
  width: 100%;

  &:last-child {
    border-bottom: none;
  }
`

const StyledItemTop = styled.div`
  display: flex;
  gap: 8px;
  width: 100%;
`

const StyledItemImage = styled.div`
  width: 70px;
  height: 51px;
  border-radius: 4px;
  overflow: hidden;
  flex-shrink: 0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const StyledItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`

const StyledItemName = styled.span`
  font-size: 16px;
  font-weight: 300;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
`

const StyledItemDescription = styled.div`
  font-size: 14px;
  font-weight: 300;
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};

  ul {
    margin: 0;
    padding: 0;
    list-style: disc;
    padding-inline-start: 21px;
  }

  li {
    margin: 0;
  }
`

const StyledItemBottom = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
`

const StyledItemPrice = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
`

const StyledAddButton = styled.button`
  width: 96px;
  height: 32px;
  border-radius: 99px;
  border: none;
  background: #0000fa;
  color: white;
  font-size: 16px;
  font-weight: 700;
  font-family: inherit;
  cursor: pointer;
`

const StyledQuantityPill = styled.div`
  width: 96px;
  height: 32px;
  border-radius: 41px;
  border: 1px solid #0e2846;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px;
`

const StyledQtyButton = styled.button`
  width: 24px;
  height: 24px;
  border-radius: 99px;
  border: none;
  background: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: inherit;
  font-size: 16px;
  padding: 0;
`

const StyledQtyValue = styled.span`
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.port.defaultTextColor};
  min-width: 40px;
  text-align: center;
`

/* eslint-disable no-unused-vars */
type ExtrasDropdownProps = {
  extras: HostServiceResponse[]
  selectedExtras: Record<number, number>
  onExtrasChange: (extras: Record<number, number>) => void
  label?: string
  placeholder?: string
}
/* eslint-enable no-unused-vars */

export default function ExtrasDropdown({
  extras,
  selectedExtras,
  onExtrasChange,
  label,
  placeholder,
}: ExtrasDropdownProps) {
  const t = useTranslations("portMeetingRoomProduct")
  const [isOpen, setIsOpen] = useState(false)
  const wrapperRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!isOpen) return
    const handleClick = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick)
    return () => document.removeEventListener("mousedown", handleClick)
  }, [isOpen])

  const selectedCount = Object.values(selectedExtras).reduce((sum, qty) => sum + qty, 0)

  const triggerSummary = useMemo(() => {
    if (selectedCount === 0) return ""
    const totalPrice = Object.entries(selectedExtras).reduce((sum, [id, qty]) => {
      const extra = extras.find((e) => e.id === Number(id))
      return sum + (extra ? extra.price * qty : 0)
    }, 0)
    return `${selectedCount} ${selectedCount === 1 ? t("extras") : t("extras")} ב- ₪${totalPrice}`
  }, [selectedExtras, extras, selectedCount, t])

  const handleAdd = (id: number) => {
    onExtrasChange({ ...selectedExtras, [id]: 1 })
  }

  const handleIncrement = (id: number) => {
    onExtrasChange({ ...selectedExtras, [id]: (selectedExtras[id] || 0) + 1 })
  }

  const handleDecrement = (id: number) => {
    const current = selectedExtras[id] || 0
    if (current <= 1) {
      const next = { ...selectedExtras }
      delete next[id]
      onExtrasChange(next)
    } else {
      onExtrasChange({ ...selectedExtras, [id]: current - 1 })
    }
  }

  return (
    <StyledWrapper ref={wrapperRef}>
      {label && (
        <span
          style={{
            display: "block",
            fontSize: 16,
            fontWeight: 300,
            lineHeight: 1.5,
            color: "#0e2846",
            marginBottom: 4,
          }}
        >
          {label}
        </span>
      )}
      <StyledTrigger
        type="button"
        $isOpen={isOpen}
        $hasValue={selectedCount > 0}
        onClick={() => setIsOpen((prev) => !prev)}
      >
        {selectedCount > 0 && (
          <StyledClearButton
            onMouseDown={(e) => {
              e.stopPropagation()
              e.preventDefault()
              onExtrasChange({})
              setIsOpen(false)
            }}
          >
            <XIcon color="#0e2846" />
          </StyledClearButton>
        )}
        <span className="trigger-text">
          {selectedCount > 0
            ? triggerSummary
            : placeholder || t("selectExtras")}
        </span>
      </StyledTrigger>
      {isOpen && (
        <StyledDropdown>
          {extras.map((extra) => {
            const qty = selectedExtras[extra.id] || 0
            return (
              <StyledItem key={extra.id}>
                <StyledItemTop>
                  <StyledItemInfo>
                    <StyledItemName>{extra.hebrewName || extra.name}</StyledItemName>
                    {(extra.hebrewDescription || extra.description) && (
                      <StyledItemDescription>
                        <ul>
                          {(extra.hebrewDescription || extra.description)
                            .split(",")
                            .map((item, i) => (
                              <li key={i}>{item.trim()}</li>
                            ))}
                        </ul>
                      </StyledItemDescription>
                    )}
                  </StyledItemInfo>
                  {(extra as any).image && (
                    <StyledItemImage>
                      <Image
                        src={(extra as any).image}
                        width={70}
                        height={51}
                        alt={extra.hebrewName || extra.name}
                      />
                    </StyledItemImage>
                  )}
                </StyledItemTop>
                <StyledItemBottom>
                  <StyledItemPrice>₪{extra.price}</StyledItemPrice>
                  {qty > 0 ? (
                    <StyledQuantityPill>
                      <StyledQtyButton onClick={() => handleDecrement(extra.id)}>
                        −
                      </StyledQtyButton>
                      <StyledQtyValue>{qty}</StyledQtyValue>
                      <StyledQtyButton onClick={() => handleIncrement(extra.id)}>
                        +
                      </StyledQtyButton>
                    </StyledQuantityPill>
                  ) : (
                    <StyledAddButton onClick={() => handleAdd(extra.id)}>
                      {t("addExtra")}
                    </StyledAddButton>
                  )}
                </StyledItemBottom>
              </StyledItem>
            )
          })}
        </StyledDropdown>
      )}
    </StyledWrapper>
  )
}
